import React, { useCallback, useState } from 'react';
import type { AgentApproval } from 'Resources/AgentApprovals';
import WriteCard from '../../Assistant/WriteCard';
import EmailDraftCard from '../../Assistant/EmailDraftCard';
import OfferDraftCard from '../../Assistant/OfferDraftCard';
import MultiActionWriteCard from '../../Assistant/MultiActionWriteCard';
import ContactScrapeCard from '../../Assistant/ContactScrapeCard';
import ProspectingAddCard from '../../Assistant/ProspectingAddCard';
import ToolProposalCard from '../../Assistant/ToolProposalCard';
import {
	CRM_ACTIONS,
	RESOLVED_STATUSES,
	approvalToWriteProposal,
	approvalToEmailDraft,
	approvalToOfferDraft,
	approvalToMultiActionConfig,
	approvalToContactScrapeConfig,
	approvalToProspectingAdd,
	PROSPECTING_ADD_ACTIONS
} from '../../Assistant/approvalAdapters';
import { approvalToToolProposal, TOOL_PROPOSAL_ACTIONS } from '../../Assistant/toolProposalAdapter';

type Props = {
	approval: AgentApproval;
	/** Called once the row reaches a terminal state, so the Inbox can refetch. */
	onResolved: () => void;
};

/**
 * The approval actions that have a card. Anything else — `webSearch` — has none
 * in the chat either, so its Inbox row keeps opening the conversation rather
 * than expanding to an apology.
 */
export const INBOX_CARD_ACTIONS = new Set([
	...CRM_ACTIONS,
	...PROSPECTING_ADD_ACTIONS,
	...TOOL_PROPOSAL_ACTIONS,
	'sendEmail',
	'draftOffer',
	'multiAction',
	'scrapeContacts'
]);

/**
 * WEB-11464 — the proposal card, rendered inside an Inbox row.
 *
 * Before this, `permissionUpgrade` was the only approval the Inbox could
 * resolve; every other kind got a row that merely navigated to its
 * conversation. Both are called "approve" in the product, so a user who
 * cleared the ones with buttons reasonably concluded they were done, while the
 * proposal that actually blocked their job sat untouched in a thread they had
 * already moved on from.
 *
 * Nothing here re-implements a card. Each one is already driven by a config
 * derived from the approval row and talks to `AgentApprovalsResource` itself,
 * which is why the same components serve the chat, the trace drawer and now
 * the Inbox. What the Inbox has to add is the piece the chat gets from its
 * message list: the cards are controlled, so somebody has to hold the patches
 * they emit while the user edits and approves.
 */
const InboxProposalCard = ({ approval, onResolved }: Props) => {
	const [patch, setPatch] = useState<Record<string, unknown>>({});

	// `onUpdate` is both the edit channel and the approve channel — every card
	// bails out of its approve handler when it is missing. Refetch only on a
	// resolved status, so a keystroke does not reload the page under the user,
	// and a failure keeps its Retry button instead of unmounting with the row.
	//
	// Memoised because ContactScrapeCard keeps `onUpdate` in the deps of the
	// effect that polls its job every 10s: a fresh identity per render restarts
	// that interval before it can ever fire.
	const applyPatch = useCallback(
		(updates: Record<string, unknown>) => {
			setPatch(prev => ({ ...prev, ...updates }));
			if (typeof updates.status === 'string' && RESOLVED_STATUSES.has(updates.status)) {
				onResolved();
			}
		},
		[onResolved]
	);
	// The two bulk cards also report which config changed. Adapting that here,
	// rather than taking `...args: unknown[]`, keeps every card's prop type
	// checked at its own call site.
	const applyConfigPatch = useCallback(
		(_id: string, updates: Record<string, unknown>) => applyPatch(updates),
		[applyPatch]
	);

	// The spread rebuilds the config on each render, which is free for the
	// scalar fields but load-bearing for `selectedIds`: MultiActionWriteCard has
	// an effect keyed on that array's identity that looks up every target by id.
	// It stays stable only because `normaliseApproval` parses `actionPayload`
	// once at fetch, so the adapter keeps handing back the same array. If that
	// parse ever moves, memoise here or the card refires a 260-id lookup per
	// render.

	if (CRM_ACTIONS.has(approval.action)) {
		return (
			<WriteCard
				proposal={{ ...approvalToWriteProposal(approval), ...patch }}
				onUpdate={applyPatch}
				defaultExpanded
			/>
		);
	}

	if (PROSPECTING_ADD_ACTIONS.has(approval.action)) {
		return (
			<ProspectingAddCard
				config={{ ...approvalToProspectingAdd(approval), ...patch }}
				onUpdate={applyConfigPatch}
				defaultExpanded
			/>
		);
	}

	if (TOOL_PROPOSAL_ACTIONS.has(approval.action)) {
		return <ToolProposalCard proposal={{ ...approvalToToolProposal(approval), ...patch }} onUpdate={applyPatch} />;
	}

	switch (approval.action) {
		case 'sendEmail':
			return <EmailDraftCard draft={{ ...approvalToEmailDraft(approval), ...patch }} onUpdate={applyPatch} />;
		case 'draftOffer':
			return <OfferDraftCard draft={{ ...approvalToOfferDraft(approval), ...patch }} onUpdate={applyPatch} />;
		case 'multiAction':
			return (
				<MultiActionWriteCard
					config={{ ...approvalToMultiActionConfig(approval), ...patch }}
					onUpdate={applyConfigPatch}
					defaultExpanded
				/>
			);
		case 'scrapeContacts':
			return (
				<ContactScrapeCard
					config={{ ...approvalToContactScrapeConfig(approval), ...patch }}
					onUpdate={applyConfigPatch}
					defaultExpanded
				/>
			);
		default:
			// Unreachable: a row only becomes expandable for the actions above
			// (INBOX_CARD_ACTIONS), so one with no card never opens.
			return null;
	}
};

export default InboxProposalCard;
